// PALMEX · Bundle ohne App (blocks/px-bundle.liquid, René 10.10.2026: ersetzt das Bundle der App Section Store).
// Rechnet das Bundle vor und legt alle Teile mit einem Tipp in den Warenkorb – mit demselben Standard-Ereignis wie
// Horizons Kaufknopf: Die Warenkorb-Schublade öffnet sich, Symbol, Warenkorbseite und Versandbalken ziehen nach.
// Den Rabatt zieht Shopify ab (automatische Rabatte „Kauf X, erhalte Y“ im Admin), hier wird er nur angezeigt:
//  - „3fuer2“: Teil 1 ist das Produkt der Seite, Teil 2 und 3 wählt man aus der Liste. Gratis ist das günstigste der
//    drei Teile – so verteilt auch Shopify „Kauf 2, erhalte 1 gratis“.
//  - „zusatz“: das Produkt der Seite plus ein festes Zusatzteil mit festem Rabatt in Euro („Betrag pro Artikel“).
// Teil 1 folgt Farbe und Größe oben auf der Seite (auch wenn Foto-Fit die Größe setzt). Neue Teile übernehmen Farbe
// und Größe von Teil 1, soweit es sie gibt und sie verfügbar sind – sonst bleibt die Größe offen und muss gewählt werden.
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const FARBE = ['color', 'colour', 'farbe', 'kleur', 'couleur'];
const GROESSE = ['size', 'größe', 'groesse', 'maat', 'taille'];
const optionsArt = (name) => {
  const n = String(name).toLowerCase().trim();
  if (FARBE.includes(n)) return 'farbe';
  if (GROESSE.includes(n)) return 'groesse';
  return n;
};

/** Varianten auspacken: [ID, Werte, Preis in Cent, verfügbar, Bildposition (0 = Hauptbild)] */
function aufbereiten(roh) {
  const bilder = roh.bilder || {};
  const varianten = roh.v.map(([id, werte, preis, frei, position]) => ({
    id,
    werte,
    preis,
    frei,
    bild: bilder[position] || bilder[0] || '',
  }));
  return {
    id: roh.id,
    titel: roh.titel,
    optionen: roh.optionen,
    arten: roh.optionen.map(optionsArt),
    werte: roh.optionen.map((_, k) => [...new Set(varianten.map((v) => v.werte[k]))]),
    varianten,
  };
}

/** Gibt es eine verfügbare Variante mit diesem Wert an Stelle k, passend zu den gewählten Werten davor? */
const moeglich = (produkt, werte, k, wert) =>
  produkt.varianten.some(
    (v) => v.frei && v.werte[k] === wert && werte.every((w, j) => j >= k || !w || v.werte[j] === w)
  );

/** Varianten, die zu den bisher gewählten Werten passen (leere Werte = egal) */
const passende = (produkt, werte) =>
  produkt.varianten.filter((v) => v.werte.every((w, k) => !werte[k] || werte[k] === w));

const varianteZu = (produkt, werte) =>
  produkt && produkt.optionen.every((_, k) => werte[k])
    ? produkt.varianten.find((v) => v.werte.every((w, k) => w === werte[k])) || null
    : null;

class PxBundle extends HTMLElement {
  #d;
  #t;
  #produkte = new Map();
  /** @type {{ produkt: any, werte: string[], wahl: boolean, rabatt: number }[]} */
  #teile = [];
  /** @type {HTMLElement[]} */
  #zeilen = [];
  /** @type {AbortController | null} */
  #abbruch = null;
  /** @type {HTMLButtonElement | null} */
  #knopf = null;
  #zeitgeber = 0;

  connectedCallback() {
    if (this.#abbruch) return;
    const daten = this.querySelector('[data-px-bundle-daten]');
    const texte = this.querySelector('[data-px-bundle-texte]');
    const liste = this.querySelector('[data-px-bundle-teile]');
    this.#knopf = this.querySelector('[data-px-bundle-knopf]');
    if (!daten || !texte || !liste || !this.#knopf) return;
    try {
      this.#d = JSON.parse(daten.textContent);
      this.#t = JSON.parse(texte.textContent);
    } catch {
      return;
    }
    this.#produkte = new Map(this.#d.produkte.map((roh) => [roh.id, aufbereiten(roh)]));
    const seite = this.#produkte.get(this.#d.seite);
    if (!seite?.varianten.length) return;

    const start =
      seite.varianten.find((v) => v.id === this.#d.variante) || seite.varianten.find((v) => v.frei) || seite.varianten[0];
    this.#teile = [{ produkt: seite, werte: [...start.werte], wahl: false, rabatt: 0 }];
    if (this.#d.art === 'zusatz') {
      const zusatz = this.#produkte.get(this.#d.zusatz);
      if (!zusatz?.varianten.length) return;
      this.#teile.push({ produkt: zusatz, werte: this.#vorbelegen(zusatz), wahl: false, rabatt: this.#d.rabatt });
    } else {
      this.#teile.push(
        { produkt: null, werte: [], wahl: true, rabatt: 0 },
        { produkt: null, werte: [], wahl: true, rabatt: 0 }
      );
    }

    this.#zeilen = this.#teile.map((_, i) => this.#zeileBauen(i));
    liste.replaceChildren(...this.#zeilen);
    this.#teile.forEach((_, i) => this.#wahlenBauen(i));
    this.#zeichnen();

    this.#abbruch = new AbortController();
    const { signal } = this.#abbruch;
    const bereich = this.closest('dialog, .shopify-section') || document;
    bereich.addEventListener(StandardEvents.productSelect, this.#hauptwahl, { signal });
    this.addEventListener('change', this.#geaendert, { signal });
    this.#knopf.addEventListener('click', this.#hinzufuegen, { signal });
  }

  disconnectedCallback() {
    this.#abbruch?.abort();
    this.#abbruch = null;
    clearTimeout(this.#zeitgeber);
  }

  #geld(cent) {
    return formatMoney(Math.max(0, cent), this.#d.geld || '{{amount_with_comma_separator}} €', this.#d.waehrung || 'EUR');
  }

  /** Text mit Platzhaltern aus Liquid: #N# Teil-Nummer, #O# Option, #P# Preis */
  #text(name, { teil = 0, option = '', preis = '' } = {}) {
    return String(this.#t[name] || '')
      .replaceAll('#N#', String(teil + 1))
      .replaceAll('#O#', option)
      .replaceAll('#P#', preis);
  }

  #optionName(produkt, k) {
    const art = produkt.arten[k];
    if (art === 'farbe') return this.#t.farbe;
    if (art === 'groesse') return this.#t.groesse;
    return produkt.optionen[k];
  }

  /** Neues Teil: Farbe und Größe wie Teil 1, wenn es sie gibt und sie verfügbar sind; eine offene Größe muss man wählen */
  #vorbelegen(produkt) {
    const vorlage = this.#teile[0];
    const werte = [];
    produkt.optionen.forEach((_, k) => {
      const art = produkt.arten[k];
      const kv = vorlage ? vorlage.produkt.arten.indexOf(art) : -1;
      const wunsch = kv >= 0 ? vorlage.werte[kv] : '';
      const frei = produkt.werte[k].filter((w) => moeglich(produkt, werte, k, w));
      if (wunsch && frei.includes(wunsch)) werte[k] = wunsch;
      else if (art === 'groesse' && produkt.werte[k].length > 1) werte[k] = '';
      else werte[k] = frei[0] || produkt.werte[k][0];
    });
    return werte;
  }

  #zeileBauen(i) {
    const teil = this.#teile[i];
    const zeile = document.createElement('li');
    zeile.className = 'px-bundle__teil';
    zeile.dataset.pxBundleTeil = String(i);

    const rahmen = document.createElement('span');
    rahmen.className = 'px-bundle__rahmen';
    const bild = document.createElement('img');
    bild.className = 'px-bundle__bild';
    bild.width = 56;
    bild.height = 70;
    bild.alt = '';
    bild.loading = 'lazy';
    bild.decoding = 'async';
    bild.hidden = true;
    const nr = document.createElement('span');
    nr.className = 'px-bundle__nr';
    nr.setAttribute('aria-hidden', 'true');
    nr.textContent = this.#d.art === 'zusatz' && i > 0 ? '+' : String(i + 1);
    rahmen.append(bild, nr);

    const name = document.createElement('p');
    name.className = 'px-bundle__name';
    if (teil.wahl) {
      const huelle = document.createElement('span');
      huelle.className = 'px-bundle__auswahl';
      const feld = document.createElement('select');
      feld.className = 'px-bundle__feld px-bundle__feld--produkt';
      feld.dataset.pxBundleProdukt = '';
      feld.setAttribute('aria-label', this.#text('teil', { teil: i }));
      const leer = new Option(this.#t.waehlen, '', true, true);
      leer.disabled = true;
      feld.append(leer, ...[...this.#produkte.values()].map((p) => new Option(p.titel, String(p.id))));
      huelle.append(feld);
      name.append(huelle);
    } else {
      name.textContent = teil.produkt.titel;
    }

    const preis = document.createElement('p');
    preis.className = 'px-bundle__preis';
    preis.dataset.pxBundlePreis = '';
    const wahlen = document.createElement('div');
    wahlen.className = 'px-bundle__wahlen';
    wahlen.dataset.pxBundleWahlen = '';

    zeile.append(rahmen, name, preis, wahlen);
    return zeile;
  }

  /** Auswahlfelder für die Optionen des gewählten Produkts (Farbe, Größe …); Optionen mit nur einem Wert entfallen */
  #wahlenBauen(i) {
    const teil = this.#teile[i];
    const box = this.#zeilen[i].querySelector('[data-px-bundle-wahlen]');
    box.replaceChildren();
    if (!teil.produkt) return;
    teil.produkt.optionen.forEach((_, k) => {
      if (teil.produkt.werte[k].length < 2) return;
      const huelle = document.createElement('span');
      huelle.className = 'px-bundle__auswahl';
      const feld = document.createElement('select');
      feld.className = 'px-bundle__feld';
      feld.dataset.pxBundleOption = String(k);
      feld.setAttribute('aria-label', `${this.#text('teil', { teil: i })}: ${this.#optionName(teil.produkt, k)}`);
      huelle.append(feld);
      box.append(huelle);
    });
  }

  #preis(teil) {
    if (!teil.produkt) return 0;
    const v = varianteZu(teil.produkt, teil.werte);
    if (v) return v.preis;
    const kandidaten = passende(teil.produkt, teil.werte);
    return Math.min(...(kandidaten.length ? kandidaten : teil.produkt.varianten).map((x) => x.preis));
  }

  #bild(teil) {
    if (!teil.produkt) return '';
    const v = varianteZu(teil.produkt, teil.werte) || passende(teil.produkt, teil.werte)[0] || teil.produkt.varianten[0];
    return v?.bild || '';
  }

  #zeichnen() {
    const preise = this.#teile.map((teil) => this.#preis(teil));
    const gewaehlt = this.#teile.filter((teil) => teil.produkt).length;
    const vollstaendig = gewaehlt === this.#teile.length;

    // Rabatt je Teil: beim Zusatzteil der feste Betrag, bei „3 für 2“ das günstigste Teil (bei Gleichstand das letzte)
    const abzug = this.#teile.map(() => 0);
    if (vollstaendig) {
      if (this.#d.art === 'zusatz') {
        abzug[1] = Math.min(this.#teile[1].rabatt, preise[1]);
      } else {
        let g = 0;
        preise.forEach((p, i) => {
          if (p <= preise[g]) g = i;
        });
        abzug[g] = preise[g];
      }
    }
    this.#teile.forEach((_, i) => this.#zeileZeichnen(i, preise[i], abzug[i]));

    const einzeln = preise.reduce((s, p) => s + p, 0);
    const rabatt = abzug.reduce((s, a) => s + a, 0);
    const summe = this.querySelector('[data-px-bundle-summe]');
    summe.hidden = !vollstaendig;
    if (vollstaendig) {
      summe.querySelector('[data-px-bundle-einzeln]').textContent = this.#geld(einzeln);
      summe.querySelector('[data-px-bundle-rabatt]').textContent = `−${this.#geld(rabatt)}`;
      summe.querySelector('[data-px-bundle-gesamt]').textContent = this.#geld(einzeln - rabatt);
    }

    // Was fehlt noch?
    const varianten = this.#teile.map((teil) => varianteZu(teil.produkt, teil.werte));
    let hinweis = '';
    if (!vollstaendig) {
      hinweis = this.#t[`noch_${this.#teile.length - gewaehlt}`] || '';
    } else {
      const offen = varianten.findIndex((v) => !v);
      const aus = varianten.findIndex((v) => v && !v.frei);
      if (offen >= 0) {
        const teil = this.#teile[offen];
        const k = teil.produkt.optionen.findIndex((_, j) => !teil.werte[j]);
        hinweis = this.#text('fehlt', { teil: offen, option: this.#optionName(teil.produkt, Math.max(0, k)) });
      } else if (aus >= 0) {
        hinweis = this.#text('aus_teil', { teil: aus });
      }
    }
    this.#knopf.disabled = !(vollstaendig && varianten.every((v) => v?.frei));
    this.#status(hinweis);
  }

  #zeileZeichnen(i, preis, abzug) {
    const teil = this.#teile[i];
    const zeile = this.#zeilen[i];
    const { produkt } = teil;

    const produktFeld = zeile.querySelector('[data-px-bundle-produkt]');
    if (produktFeld) {
      produktFeld.value = produkt ? String(produkt.id) : '';
      produktFeld.toggleAttribute('data-leer', !produkt);
    }

    for (const feld of zeile.querySelectorAll('[data-px-bundle-option]')) {
      const k = Number(feld.dataset.pxBundleOption);
      const wert = teil.werte[k] || '';
      const optionen = [];
      if (!wert) {
        const text = produkt.arten[k] === 'groesse' ? this.#t.groesse_waehlen : `${this.#optionName(produkt, k)} …`;
        const leer = new Option(text, '', true, true);
        leer.disabled = true;
        optionen.push(leer);
      }
      for (const w of produkt.werte[k]) {
        const frei = moeglich(produkt, teil.werte, k, w);
        const option = new Option(frei ? w : `${w} – ${this.#t.ausverkauft}`, w, false, w === wert);
        option.disabled = !frei && w !== wert;
        optionen.push(option);
      }
      feld.replaceChildren(...optionen);
      feld.value = wert;
      feld.toggleAttribute('data-leer', !wert);
    }

    const bild = zeile.querySelector('.px-bundle__bild');
    const quelle = this.#bild(teil);
    if (quelle) {
      if (bild.getAttribute('src') !== quelle) bild.src = quelle;
      bild.hidden = false;
    } else {
      bild.hidden = true;
      bild.removeAttribute('src');
    }

    const preisFeld = zeile.querySelector('[data-px-bundle-preis]');
    const gratis = abzug > 0 && abzug >= preis;
    zeile.toggleAttribute('data-gratis', gratis);
    if (!produkt) {
      preisFeld.replaceChildren();
    } else if (abzug > 0) {
      const neu = document.createElement('strong');
      neu.textContent = gratis ? this.#t.gratis : this.#geld(preis - abzug);
      const sonst = document.createElement('small');
      sonst.textContent = this.#text('sonst', { preis: this.#geld(preis) });
      preisFeld.replaceChildren(neu, sonst);
    } else {
      preisFeld.textContent = this.#geld(preis);
    }
  }

  #status(text, fehler = false) {
    const feld = this.querySelector('[data-px-bundle-status]');
    if (!feld) return;
    if (feld.textContent !== text) feld.textContent = text;
    feld.toggleAttribute('data-fehler', fehler);
  }

  /** Auswahl oben auf der Seite (Horizon-Variantenwahl) → Teil 1 */
  #hauptwahl = async (event) => {
    const quelle = event.target instanceof Element ? event.target : null;
    if (!quelle || quelle.closest('product-card, px-bundle')) return;
    let ergebnis;
    try {
      ergebnis = await event.promise;
    } catch {
      return;
    }
    const id = Number(ergebnis?.detail?.resource?.id);
    const teil = this.#teile[0];
    const v = teil?.produkt.varianten.find((x) => x.id === id);
    if (!v) return;
    teil.werte = [...v.werte];
    this.#zeichnen();
  };

  #geaendert = (event) => {
    const feld = event.target;
    if (!(feld instanceof HTMLSelectElement)) return;
    const i = Number(feld.closest('[data-px-bundle-teil]')?.dataset.pxBundleTeil);
    const teil = this.#teile[i];
    if (!teil) return;
    if ('pxBundleProdukt' in feld.dataset) {
      teil.produkt = this.#produkte.get(Number(feld.value)) || null;
      teil.werte = teil.produkt ? this.#vorbelegen(teil.produkt) : [];
      this.#wahlenBauen(i);
    } else {
      const k = Number(feld.dataset.pxBundleOption);
      teil.werte[k] = feld.value;
      // Spätere Werte, die es so nicht (mehr) gibt, wieder öffnen – z. B. eine Größe, die in der neuen Farbe fehlt
      for (let j = k + 1; j < teil.werte.length; j++) {
        if (teil.werte[j] && teil.produkt.werte[j].length > 1 && !moeglich(teil.produkt, teil.werte, j, teil.werte[j])) {
          teil.werte[j] = '';
        }
      }
    }
    this.#zeichnen();
  };

  #hinzufuegen = async () => {
    const knopf = this.#knopf;
    const varianten = this.#teile.map((teil) => varianteZu(teil.produkt, teil.werte));
    if (!knopf || knopf.hasAttribute('aria-busy') || !varianten.every((v) => v?.frei)) return;

    const mengen = new Map();
    for (const v of varianten) mengen.set(v.id, (mengen.get(v.id) || 0) + 1);
    const posten = [...mengen].map(([id, quantity]) => ({ id, quantity }));
    const sektionen = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    const aufgeschoben = CartLinesUpdateEvent.createPromise();
    knopf.setAttribute('aria-busy', 'true');
    this.#status('');

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: posten.map(({ id, quantity }) => ({ merchandiseId: String(id), quantity })),
        promise: aufgeschoben.promise,
      })
    );

    try {
      const antwort = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: posten, sections: sektionen.join(',') }),
      }).then((r) => r.json());
      const korb = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());

      if (antwort.status) {
        this.dispatchEvent(
          new CartErrorEvent({ error: antwort.message || 'Add to cart failed', code: 'INVALID' })
        );
        this.#status(antwort.description || this.#t.fehler, true);
      } else {
        knopf.textContent = this.#t.im_korb;
        clearTimeout(this.#zeitgeber);
        this.#zeitgeber = setTimeout(() => (knopf.textContent = this.#t.hinzufuegen), 2500);
      }
      aufgeschoben.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(korb),
        detail: {
          items: korb.items,
          source: 'px-bundle',
          sections: antwort.sections,
          didError: Boolean(antwort.status),
        },
      });
    } catch (fehler) {
      aufgeschoben.reject(fehler);
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error while adding to cart', code: 'SERVICE_UNAVAILABLE' }));
      this.#status(this.#t.fehler, true);
    } finally {
      knopf.removeAttribute('aria-busy');
    }
  };
}

if (!customElements.get('px-bundle')) customElements.define('px-bundle', PxBundle);
